/**
 * Адреса раздела «Финансы»: вкладка — в пути, период и отбор — в строке
 * запроса. `/finance/checks?from=2026-09-01&to=2026-09-07&category=1003`.
 *
 * Правило то же, что у дашборда (§14.4 ТЗ `finance`): ссылка из бота
 * открывает ровно тот разрез, о котором сообщение, и «назад» браузера
 * возвращает к предыдущему.
 */

import type { PeriodParams } from '@finance/api';
import type { CheckFilters } from '@finance/screens/Checks';

/**
 * `right` — раздел прав финансового агента (D230). Меню ABC и матрица
 * строятся на марже, поэтому «Меню» открывается себестоимостью.
 */
export const TABS = [
  { code: 'overview', title: 'Обзор', right: 'overview' },
  { code: 'sales', title: 'Продажи', right: 'sales' },
  { code: 'cost', title: 'Себестоимость', right: 'cost' },
  { code: 'menu', title: 'Меню', right: 'menu' },
  { code: 'checks', title: 'Чеки', right: 'checks' },
  { code: 'control', title: 'Контроль', right: 'control' },
  { code: 'data', title: 'Данные', right: 'data' },
] as const;

export type TabCode = (typeof TABS)[number]['code'];

/** Вкладки, открытые человеку. Сторожит сервер; здесь — что нарисовать. */
export function openTabs(
  rights: Record<string, string> | undefined,
  isHost: boolean,
): (typeof TABS)[number][] {
  return TABS.filter((tab) => isHost || Boolean(rights?.[tab.right]));
}

export interface FinanceState {
  tab: TabCode;
  /** `rules` — «Контроль → Пороги и цели». */
  view: 'feed' | 'rules';
  period: PeriodParams;
  filters: CheckFilters;
}

const KEYS = ['from', 'to', 'grain', 'waiter', 'category', 'item'] as const;

export function readFinance(path: string, params: URLSearchParams): FinanceState {
  const [, , rawTab, rawView] = path.split('/');
  const tab = (TABS.find((item) => item.code === rawTab)?.code ?? 'overview') as TabCode;
  const value = (key: string) => params.get(key) || undefined;
  return {
    tab,
    view: tab === 'control' && rawView === 'rules' ? 'rules' : 'feed',
    period: { from: value('from'), to: value('to'), grain: value('grain') },
    filters: { waiter: value('waiter'), category: value('category'), item: value('item') },
  };
}

export function financePath(state: FinanceState): string {
  const params = new URLSearchParams();
  const flat: Record<string, string | undefined> = { ...state.period, ...state.filters };
  for (const key of KEYS) {
    const value = flat[key];
    if (value) params.set(key, value);
  }
  const view = state.tab === 'control' && state.view === 'rules' ? '/rules' : '';
  const search = params.toString();
  return `/finance/${state.tab}${view}${search ? `?${search}` : ''}`;
}
